import React, { useEffect, useState } from 'react';
import { Avatar, Button, Card, Col, Divider, List, Row, Typography, message } from 'antd';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ContentLoader from 'react-content-loader';
import DetailsGrid from 'components/UtilComponents/DetailsGrid';
import NumberText from 'components/UtilComponents/NumberText';
import { ORDER_URL, UPLOAD_URL } from 'constants/ApiConstants';
import { formatQuantity, formatRefNo } from 'utils/formatter';
import CreateOpenListing from 'components/Modals/Sell/CreateOpenListing';
import IconOrders from 'assets/images/orders.png';
import EmptyContent from 'components/UtilComponents/EmptyContent';
import { getBreakPoint } from 'utils/layout';
import { isEmpty } from 'lodash';
import UpdateOrderStatus from 'components/Modals/Sell/UpdateOrderStatus';

const { Text, Title } = Typography;

const OngoingOrders = ({ tab }) => {
  const isMobile = !getBreakPoint().includes('sm');
  const navigate = useNavigate();
  const [showOpenListing, setShowOpenListing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [order, setOrder] = useState({});
  const [dispatchVisible, setDispatchVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 8,
    total: 0,
    size: 'middle',
    showSizeChanger: false,
  });

  useEffect(() => {
    if (tab === 'active-orders') {
      onLoadOrders();
    } else {
      setPagination({ ...pagination, current: 1 });
    }
  }, [tab, pagination.current]);

  const onLoadOrders = async () => {
    setLoading(true);
    try {
      let currentPage = pagination.current;
      const localData = await JSON.parse(localStorage.getItem('filters'));
      if (!isEmpty(localData)) {
        currentPage = localData.current;
      }

      const res = await axios.get(`${ORDER_URL}?page=${currentPage}&limit=${pagination.pageSize}&populate=true`, {
        headers: {
          'X-API-Filters': JSON.stringify({ seller: ['me'], status: ['In Progress'] }),
          'X-API-Sort': JSON.stringify({ createdAt: 'desc' }),
        },
      });
      if (res.data.status === 'error') {
        message.error(res.data.error);
      } else {
        setOrders(res.data.orders);
        setPagination({
          ...pagination,
          current: currentPage,
          total: res.data.count,
        });
      }
      localStorage.removeItem('filters');
    } catch (err) {
      message.error(err.message);
    }
    setLoading(false);
  };

  const onOpenDispatch = (order) => {
    setOrder(order);
    setDispatchVisible(true);
  };

  const onCloseDispatch = (reload) => {
    if (reload) {
      onLoadOrders();
    }
    setDispatchVisible(false);
  };

  const onViewOrders = (orderId) => {
    localStorage.setItem('filters', JSON.stringify({ current: pagination.current }));
    navigate(`/sell/orders/active-orders/${orderId}`);
  };

  const onCreateWholesaleOpenListing = () => {
    setShowOpenListing(!showOpenListing);
  };

  if (loading) {
    return (
      <ContentLoader width="100%" height="70vh" backgroundColor="#d9d9d9" foregroundColor="#ededed">
        <rect x="0" y="25" rx="10" ry="10" width="48%" height="211" />
        <rect x="50%" y="25" rx="10" ry="10" width="50%" height="211" />
        <rect x="0" y="250" rx="10" ry="10" width="48%" height="211" />
        <rect x="50%" y="250" rx="10" ry="10" width="50%" height="211" />
      </ContentLoader>
    );
  }

  return (
    <>
      {orders?.length > 0 ? (
        <List
          className="my-4"
          header={null}
          footer={null}
          grid={{ gutter: [16, 4], column: isMobile ? 1 : 2 }}
          dataSource={orders}
          pagination={{ ...pagination, onChange: (page) => setPagination({ ...pagination, current: page }) }}
          renderItem={(order) => {
            const { product } = order;
            return (
              <List.Item>
                <Card size={isMobile ? 'small' : 'default'}>
                  <Row align="middle" justify="flex-start" className="mb-3">
                    <Col span={18}>
                      <Row gutter={10} justify="flex-start" wrap={false} className="mb-3">
                        <Col>
                          <Avatar src={`${UPLOAD_URL}/${order?.product?.image}`} shape="square" size="large" />
                        </Col>
                        <Col>
                          <Title level={5} className="fw-medium">
                            {product?.name}
                          </Title>
                          <NumberText value={order?.purchasingPrice / 100} prefix={order?.currency} suffix={`/ ${order?.subUnit}`} />
                        </Col>
                      </Row>
                    </Col>
                    <Col flex={1} className="d-flex justify-content-end">
                      <Button type="primary" onClick={() => onViewOrders(order?._id)}>
                        View
                      </Button>
                    </Col>
                  </Row>
                  <DetailsGrid
                    icon={<Avatar src={require('assets/images/po-Number.png')} shape="square" size="small" />}
                    title="Order Number"
                    description={<Text strong>{formatRefNo(order?.orderRefNo)}</Text>}
                  />
                  <Divider dashed={true} className="my-3" />
                  <Row gutter={16} className="mt-3">
                    <Col span={12}>
                      <DetailsGrid
                        icon={<Avatar src={require('assets/images/total-quantity.png')} shape="square" size="small" />}
                        title="Total Quantity"
                        description={
                          <Text strong>
                            {formatQuantity(order?.qty) || '∞'} {order?.subUnit}
                          </Text>
                        }
                      />
                    </Col>
                    <Col span={12}>
                      <DetailsGrid
                        icon={<Avatar src={require('assets/images/dispatched-quantity.png')} shape="square" size="small" />}
                        title="Dispatch Quantity"
                        description={
                          <Text strong>
                            {formatQuantity(order?.metadata?.dispatchedQty)}
                            {order?.subUnit}
                          </Text>
                        }
                      />
                    </Col>
                  </Row>

                  <Divider dashed={true} className="my-3" />
                  <Row>
                    {/* <Col xs={24} sm={24} md={24} lg={14}>
                      {order?.paymentPriceBasis?.pendingPayable.value ? (
                        <Text>
                          <NumberText value={order?.paymentPriceBasis?.pendingPayable.value / 100} prefix={order?.currency} />
                          &nbsp;Payment Pending
                        </Text>
                      ) : (
                        <Text>No Payments Pending</Text>
                      )}
                    </Col> */}

                    <Col flex={1} className="d-flex justify-content-end">
                      <Button
                        type="primary"
                        ghost
                        onClick={() => {
                          onOpenDispatch(order);
                        }}>
                        Create Dispatch
                      </Button>
                    </Col>
                  </Row>
                </Card>
              </List.Item>
            );
          }}
        />
      ) : (
        <EmptyContent source={IconOrders} title={'No Active Orders'} subTitle={'There are no ongoing orders found right now.'} />
      )}
      <UpdateOrderStatus order={order} visible={dispatchVisible} close={onCloseDispatch} />
      <CreateOpenListing visible={showOpenListing} close={onCreateWholesaleOpenListing} />
    </>
  );
};

export default OngoingOrders;
